import {
  SettingOutlined,
  CloseOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { captureException } from "@sentry/nextjs";
import {
  App,
  Modal,
  Button,
  Space,
  Typography,
  Alert,
  Select,
  Tag,
} from "antd";
import { debounce } from "lodash";
import dynamic from "next/dynamic";
import { memo, useState, useCallback, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocalStorage } from "react-use";

import { CurrencyFragment } from "../../lib/graphql";
import { useCurrentLocale } from "../../lib/hooks/basic/locales";
import { useCurrentUser } from "../../lib/hooks/basic/users";
import { useIsDark } from "../../lib/hooks/theme";
import { filterFalse } from "../../lib/utils";

const TVTicker = dynamic(
  () => import("react-ts-tradingview-widgets").then((w) => w.Ticker),
  {
    ssr: false,
  }
);

const titles = defineMessages({
  sp500: { id: "tickers.sp500", defaultMessage: "S&P 500 Index" },
  coffee: { id: "tickers.coffee", defaultMessage: "Coffee" },
  cocoa: { id: "tickers.cocoa", defaultMessage: "Cocoa" },
  sugar: { id: "tickers.sugar", defaultMessage: "Sugar" },
  oil: { id: "tickers.oil", defaultMessage: "Oil" },
  currency: { id: "tickers.currency", defaultMessage: "Currency" },
  customizeTickers: {
    id: "tickers.customizeTickers",
    defaultMessage: "Customize Tickers",
  },
  resetToDefaults: {
    id: "tickers.resetToDefaults",
    defaultMessage: "Reset to Defaults",
  },
  close: { id: "common.close", defaultMessage: "Close" },
  searchTickers: {
    id: "tickers.searchTickers",
    defaultMessage: "Search for tickers (min 2 characters)...",
  },
  addCustomTicker: {
    id: "tickers.addCustomTicker",
    defaultMessage: "Add Custom Ticker",
  },
  searchFailed: {
    id: "tickers.searchFailed",
    defaultMessage: "Failed to search symbols",
  },
  tickerAlreadyAdded: {
    id: "tickers.tickerAlreadyAdded",
    defaultMessage: "This ticker is already added",
  },
  maxTickersReached: {
    id: "tickers.maxTickersReached",
    defaultMessage: "Maximum 15 tickers can be selected",
  },
  removeTicker: { id: "tickers.removeTicker", defaultMessage: "Remove ticker" },
  minOneTicker: {
    id: "tickers.minOneTicker",
    defaultMessage: "At least one ticker must be selected",
  },
  displayLimitWarning: {
    id: "tickers.displayLimitWarning",
    defaultMessage:
      "You have selected more than 6 tickers. Only the first 6 will be visible on most screens. Reorder them to prioritize which ones appear first.",
  },
  searching: { id: "tickers.searching", defaultMessage: "Searching..." },
  searchMinChars: {
    id: "tickers.searchMinChars",
    defaultMessage: "Type at least 2 characters",
  },
  selectedTickers: {
    id: "tickers.selectedTickers",
    defaultMessage: "Selected Tickers ({count}/15)",
  },
  noTickersSelected: {
    id: "tickers.noTickersSelected",
    defaultMessage: "No tickers selected. Search and add tickers above.",
  },
});

interface TickerDefinition {
  proName: string;
  title?: string;
  titleKey?: keyof typeof titles;
  requiresCurrency?: boolean;
}

interface SymbolSearchResult {
  symbol: string;
  description: string;
  type: string;
  exchange: string;
  source2?: {
    id: string;
    name: string;
    description: string;
  };
  currency_code?: string;
  isin?: string;
}

const DEFAULT_TICKERS: TickerDefinition[] = [
  {
    proName: "FOREXCOM:SPXUSD",
    titleKey: "sp500",
  },
  {
    proName: "FX_IDC:USD{CODE}",
    titleKey: "currency",
    requiresCurrency: true,
  },
  {
    proName: "BLACKBULL:WTI",
    titleKey: "oil",
  },
  {
    proName: "PEPPERSTONE:COFFEE",
    titleKey: "coffee",
  },
  {
    proName: "PEPPERSTONE:COCOA",
    titleKey: "cocoa",
  },
  {
    proName: "PEPPERSTONE:SUGAR",
    titleKey: "sugar",
  },
];

const stripHtml = (str: string) => str.replace(/<\/?em>/g, "");

const TickerWidget = memo(({ symbols }: { symbols: any[] }) => {
  const isDark = useIsDark();

  return (
    <TVTicker
      isTransparent
      colorTheme={isDark ? "dark" : "light"}
      copyrightStyles={{ parent: { display: "none" } }}
      symbols={symbols}
    />
  );
});

interface SortableTickerItemProps {
  id: string;
  children: React.ReactNode;
  onRemove: () => void;
  disabled?: boolean;
}

const SortableTickerItem = memo(function SortableTickerItem({
  id,
  children,
  onRemove,
  disabled,
}: SortableTickerItemProps) {
  const intl = useIntl();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 8px",
    border: "1px solid var(--ant-color-border)",
    borderRadius: "4px",
    backgroundColor: "var(--ant-color-bg-container)",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
        <HolderOutlined
          {...attributes}
          {...listeners}
          style={{
            cursor: isDragging ? "grabbing" : "grab",
            color: "var(--ant-color-text-tertiary)",
          }}
        />
        <span>{children}</span>
      </div>
      <Button
        type="text"
        size="small"
        danger
        icon={<CloseOutlined />}
        onClick={onRemove}
        disabled={disabled}
        title={
          disabled
            ? intl.formatMessage(titles.minOneTicker)
            : intl.formatMessage(titles.removeTicker)
        }
      />
    </div>
  );
});

interface TickerSettingsModalProps {
  open: boolean;
  onClose: () => void;
  tickers: TickerDefinition[];
  onSave: (tickers: TickerDefinition[]) => void;
  currency: CurrencyFragment;
}

function TickerSettingsModal({
  open,
  onClose,
  tickers,
  onSave,
  currency,
}: TickerSettingsModalProps) {
  const { currentLocale } = useCurrentLocale();
  const intl = useIntl();
  const { message } = App.useApp();
  const [searchResults, setSearchResults] = useState<SymbolSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const searchTickers = useCallback(
    debounce(async (query: string) => {
      try {
        const response = await fetch(
          `https://symbol-search.tradingview.com/local_search/v3/?text=${encodeURIComponent(
            query
          )}&hl=1&exchange=&lang=${currentLocale}&search_type=&promo=true`,
          {
            headers: {
              "sec-ch-ua":
                '"Chromium";v="142", "Google Chrome";v="142", "Not_A Brand";v="99"',
              "sec-ch-ua-mobile": "?0",
              "sec-ch-ua-platform": '"macOS"',
            },
            referrer: "https://www.tradingview.com/",
            method: "GET",
            mode: "cors",
            credentials: "omit",
          }
        );
        const data = await response.json();
        setSearchResults((data.symbols || []).slice(0, 10));
      } catch (error) {
        message.error(intl.formatMessage(titles.searchFailed));
        captureException(error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 500),
    [currentLocale, message]
  );

  const handleSearch = useCallback(
    (query: string) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        setSearching(false);
        return;
      }
      setSearchResults([]);
      setSearching(true);
      searchTickers(query);
    },
    [searchTickers]
  );

  const handleAddTicker = useCallback(
    (result: SymbolSearchResult) => {
      const cleanSymbol = stripHtml(result.symbol);
      const cleanDescription = stripHtml(result.description);

      const tvSymbol = `${result.exchange}:${cleanSymbol}`;

      const newTicker: TickerDefinition = {
        proName: tvSymbol,
        title: `${cleanDescription} (${
          result.source2?.name || result.exchange
        })`,
      };

      if (tickers.some((t) => t.proName === tvSymbol)) {
        message.warning(intl.formatMessage(titles.tickerAlreadyAdded));
        return;
      }

      if (tickers.length >= 15) {
        message.warning(intl.formatMessage(titles.maxTickersReached));
        return;
      }

      onSave([...tickers, newTicker]);
      message.success(`Added ${cleanDescription}`);
    },
    [tickers, intl, onSave, message]
  );

  const handleRemoveTicker = useCallback(
    (proName: string) => {
      onSave(tickers.filter((t) => t.proName !== proName));
    },
    [tickers, onSave]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = tickers.findIndex((t) => t.proName === active.id);
        const newIndex = tickers.findIndex((t) => t.proName === over.id);
        const newOrder = arrayMove(tickers, oldIndex, newIndex);
        onSave(newOrder);
      }
    },
    [tickers, onSave]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleResetToDefaults = useCallback(() => {
    onSave(DEFAULT_TICKERS);
  }, [onSave]);

  const searchOptions = useMemo(
    () =>
      searchResults.map((item) => ({
        value: item.symbol,
        label: (
          <div>
            <div dangerouslySetInnerHTML={{ __html: item.description }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {stripHtml(item.symbol)} · {item.source2?.name || item.exchange}
              {item.currency_code ? ` · ${item.currency_code}` : ""}
            </Typography.Text>
          </div>
        ),
        data: item,
      })),
    [searchResults]
  );

  return (
    <Modal
      title={intl.formatMessage(titles.customizeTickers)}
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="reset" onClick={handleResetToDefaults}>
          {intl.formatMessage(titles.resetToDefaults)}
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          {intl.formatMessage(titles.close)}
        </Button>,
      ]}
    >
      <Space orientation="vertical" style={{ width: "100%" }} size="large">
        {/* Search for tickers */}
        <div>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            {intl.formatMessage(titles.addCustomTicker)}
          </Typography.Text>
          <Select
            showSearch
            placeholder={intl.formatMessage(titles.searchTickers)}
            style={{ width: "100%" }}
            filterOption={false}
            onSearch={handleSearch}
            loading={searching}
            notFoundContent={
              searching
                ? intl.formatMessage(titles.searching)
                : intl.formatMessage(titles.searchMinChars)
            }
            options={searchOptions}
            onSelect={(_value: any, option: any) =>
              handleAddTicker(option.data)
            }
            value={null}
            disabled={tickers.length >= 15}
          />
        </div>

        {/* Selected tickers */}
        <div>
          <Typography.Text strong>
            {intl.formatMessage(titles.selectedTickers, {
              count: tickers.length,
            })}
          </Typography.Text>
          {tickers.length === 0 ? (
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginTop: 8 }}
            >
              {intl.formatMessage(titles.noTickersSelected)}
            </Typography.Text>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={tickers.map((t) => t.proName)}
                strategy={verticalListSortingStrategy}
              >
                <div
                  style={{ marginTop: 8, maxHeight: 300, overflowY: "auto" }}
                >
                  <Space orientation="vertical" style={{ width: "100%" }}>
                    {tickers.map((ticker) => {
                      if (
                        ticker.requiresCurrency &&
                        currency.isoCode === "USD"
                      ) {
                        return null;
                      }

                      const proName = ticker.proName;

                      return (
                        <SortableTickerItem
                          key={proName}
                          id={proName}
                          onRemove={() => handleRemoveTicker(proName)}
                          disabled={tickers.length <= 1}
                        >
                          {ticker.requiresCurrency
                            ? currency.name
                            : ticker.titleKey
                              ? intl.formatMessage(titles[ticker.titleKey])
                              : ticker.title}{" "}
                          <Tag>
                            {ticker.requiresCurrency
                              ? ticker.proName.replace(
                                  "{CODE}",
                                  currency.isoCode
                                )
                              : ticker.proName}
                          </Tag>
                        </SortableTickerItem>
                      );
                    })}
                  </Space>
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Warning for ticker limit */}
        {tickers.length > 6 && (
          <Alert
            description={intl.formatMessage(titles.displayLimitWarning)}
            type="warning"
          />
        )}
      </Space>
    </Modal>
  );
}

function Ticker() {
  const intl = useIntl();
  const { currentTenant } = useCurrentUser();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [activeTickers, setActiveTickers] = useLocalStorage<TickerDefinition[]>(
    "ticker:v1",
    DEFAULT_TICKERS
  );

  const tickers = useMemo(
    () => activeTickers || DEFAULT_TICKERS,
    [activeTickers]
  );

  const symbols = useMemo(() => {
    return filterFalse(
      tickers.map((ticker) => {
        if (ticker.requiresCurrency && currentTenant.currencyCode === "USD") {
          return false;
        }

        if (ticker.requiresCurrency) {
          return {
            proName: ticker.proName.replace(
              "{CODE}",
              currentTenant.currencyCode
            ),
            title: currentTenant.currencyCode,
          };
        }

        if (ticker.titleKey) {
          return {
            proName: ticker.proName,
            title: intl.formatMessage(titles[ticker.titleKey]),
          };
        }

        return {
          proName: ticker.proName,
          title: ticker.title || ticker.proName,
        };
      })
    );
  }, [tickers, currentTenant.currencyCode, intl]);

  const handleSave = useCallback(
    (newTickers: TickerDefinition[]) => {
      setActiveTickers(newTickers);
    },
    [setActiveTickers]
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1 }}>
        <TickerWidget symbols={symbols} />
      </div>

      <Button
        icon={<SettingOutlined />}
        size="small"
        onClick={() => setSettingsOpen(true)}
      />

      <TickerSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        tickers={tickers}
        onSave={handleSave}
        currency={currentTenant.currency}
      />
    </div>
  );
}

export default memo(Ticker);
